import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import * as contactosApi from '@/modules/bd-unificada/api/contactosApi'
import type {
  ContactosFilters,
  ContactoUpdateInput,
} from '@/modules/bd-unificada/types/contacto'

export const contactosKeys = {
  all: ['bd-unificada', 'contactos'] as const,
  lists: () => [...contactosKeys.all, 'list'] as const,
  list: (filters?: ContactosFilters) => [...contactosKeys.lists(), { filters }] as const,
  search: (q: string) => [...contactosKeys.all, 'search', q] as const,
  detail: (id: string) => [...contactosKeys.all, 'detail', id] as const,
  stats: () => [...contactosKeys.all, 'stats'] as const,
  especialidades: () => [...contactosKeys.all, 'especialidades'] as const,
}

export function useContactos(filters?: ContactosFilters) {
  return useQuery({
    queryKey: contactosKeys.list(filters),
    queryFn: () => contactosApi.getContactos(filters),
  })
}

export function useBuscarContactos(q: string) {
  return useQuery({
    queryKey: contactosKeys.search(q),
    queryFn: () => contactosApi.buscarContactos(q),
    enabled: q.trim().length >= 2,
  })
}

export function useStats() {
  return useQuery({
    queryKey: contactosKeys.stats(),
    queryFn: () => contactosApi.getStats(),
  })
}

export function useEspecialidades() {
  return useQuery({
    queryKey: contactosKeys.especialidades(),
    queryFn: () => contactosApi.getEspecialidades(),
    staleTime: 1000 * 60 * 60,
  })
}

export function useContacto(id: string) {
  return useQuery({
    queryKey: contactosKeys.detail(id),
    queryFn: () => contactosApi.getContacto(id),
    enabled: !!id,
  })
}

export function useUpdateContacto() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: ContactoUpdateInput }) =>
      contactosApi.updateContacto(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: contactosKeys.lists() })
      queryClient.invalidateQueries({ queryKey: contactosKeys.detail(variables.id) })
      toast.success('Contacto actualizado exitosamente')
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'Error al actualizar contacto')
    },
  })
}

export function useDeleteContacto() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => contactosApi.deleteContacto(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: contactosKeys.lists() })
      queryClient.invalidateQueries({ queryKey: contactosKeys.stats() })
      toast.success('Contacto eliminado exitosamente')
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'Error al eliminar contacto')
    },
  })
}

export function useExportarCsv() {
  return useMutation({
    mutationFn: (filters?: ContactosFilters) => contactosApi.exportarContactosCsv(filters),
    onSuccess: (blob) => {
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = 'contactos.csv'
      document.body.appendChild(a)
      a.click()
      a.remove()
      window.URL.revokeObjectURL(url)
      toast.success('Exportación iniciada')
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'Error al exportar contactos')
    },
  })
}
